import React, { useState } from "react";
import { Button, Flex, Paper, Text, Title } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { LikeResponse } from "../../../entities/components/LikeResponse";
import useReportStore from "../../../store/useReportStore";
import { JobPositionData, UnexpectedRole } from "../../../types/api";
import { getJobDetailsByTitle } from "../../../api/reportApi";
import { JobDetailsModal } from "../../../utils/components/JobDetailsModal";
import { useTranslation } from "react-i18next";
import useProfileStore from "../../../store/useProfileStore";


interface Role {
  title: string;
  bgColor: string;
  description: string;
}

function mapUnexpectedRoles(roles: UnexpectedRole[]): Role[] {
  const roleColors = ["#3A8BD9", "#6F40E2", "#A64DD7"];

  return roles.map((role, index) => ({
    title: role.title,
    bgColor: roleColors[index % roleColors.length],
    description: role.description,
  }));
}

export const RolesToConsider: React.FC = () => {
  const matches = useMediaQuery("(max-width: 1100px)");
  const { report, reportId } = useReportStore();
  const [opened, setOpened] = useState(false);
  const [currentJob, setCurrentJob] = useState<JobPositionData | null>(null);
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>(
    {}
  );
  const { t } = useTranslation();

  const { profileLanguage } = useProfileStore();

  const handleOpen = async (title: string) => {
    setLoadingStates((prev) => ({ ...prev, [title]: true }));
    try {
      const res = await getJobDetailsByTitle(
        title,
        Object.keys(profileLanguage!)[0]
      );
      if (res) {
        setCurrentJob(res.data);
        setOpened(true);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingStates((prev) => ({ ...prev, [title]: false }));
    }
  };

  const content = report?.unexpected_roles || [];
  const rolesToConsider = mapUnexpectedRoles(content.data);

  return (
    <Paper radius="24px" p="32px" mt={32} withBorder>
      <Title size={matches ? 24 : 36} mb={8}>
        {content?.mark_down ? content?.mark_down.replace("##", "") : ""}
      </Title>
      <Text style={{ fontSize: matches ? 16 : 18 }} mb={24} c="dimmed">
        {content?.explanation ? content?.explanation : ""}
      </Text>
      <Flex
        c="#fff"
        className="blocks"
        direction={matches ? "column" : undefined}
      >
        {rolesToConsider.map((role, index) => (
          <Paper
            key={index}
            bg={role.bgColor}
            p="24px"
            radius={16}
            className={`block block-${index + 1}`}
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <div>
              <Title order={3}>{role.title}</Title>
              <Text size={matches ? "m" : "lg"} mt={12}>
                {role.description}
              </Text>
            </div>

            <Button
              mt={30}
              variant="white"
              style={{
                alignSelf: "flex-end",
              }}
              size="sm"
              loading={loadingStates[role.title]}
              onClick={() => handleOpen(role.title)}
            >
              {t("learn_more")}
            </Button>
          </Paper>
        ))}
      </Flex>
      <JobDetailsModal
        opened={opened}
        job={currentJob!}
        onClose={() => {
          setOpened(false);
          setCurrentJob(null);
        }}
        setCurrentJob={setCurrentJob}
      />
      <LikeResponse report_id={reportId} section="unexpected_roles" />
    </Paper>
  );
};
